/**
 * RuntimeStore - UI state layer
 *
 * Stores:
 * - definitions: GraphDefinition objects directly from runtime
 * - positions: node positions per definition
 * - viewports: viewport state per definition
 * - values: live endpoint values per instance
 *
 * Key principle: Runtime is single source of truth.
 * Definitions are synced from runtime, values are tracked per-instance.
 */

import type { ValueChangedArgs } from './Runtime';
import { type GraphDefinition, getNodeType, getNodeLabel } from '../graph/GraphDefinition';
import { autoLayoutFromEdges } from '../ui/components/graph/autoLayout';
import { NodeDef } from './IRuntime';
import { params } from '@/nodes/TypedEndpoints';
import type { PluginRegistry } from '@/plugins/Plugin';
import { LogLevel } from '@/nodes/INode';
import type { Endpoint } from '@/nodes/Endpoint';
import { Connection } from '@/graph/Connection';
import { SessionState } from '@/server/SessionState';
import { logger } from '@/logging';
import { EndpointState } from '@/nodes/Endpoint';

// ============ Types ============

export interface Position {
  x: number;
  y: number;
  isCollapsed?: boolean;
}

export interface ViewportState {
  x: number;
  y: number;
  zoom: number;
}

export type DefaultView = 'graph' | '2d' | '3d';

export interface OrbitState {
  theta: number;
  phi: number;
  radius: number;
  panX: number;
  panY: number;
  panZ: number;
  roll: number;
}

export interface PerformanceViewState {
  cameras: { [name: string]: OrbitState };
  activeCamera: string;
}

export interface NodeTypeInfo {
  type: string;
  shortName?: string;
  label?: string;  // Display label for UI
  info?: string;   // Help text shown in tooltip/help page
  domain?: string;
  category?: string;  // Sub-category within domain (e.g., 'source', 'effect')
  keywords?: string[];  // Additional search terms for node menu
}

export interface MenuState {
  libDefIds: string[];
  projectDefIds: string[];
  primitiveDefs: NodeTypeInfo[];
}




export interface NodeState {
  id?: string;
  type: string;
  label?: string;
  /** Help text shown in info popup */
  info?: string;
  endpoints: Record<string, EndpointState>;
  logLevel?: LogLevel;
  /** Whether this node allows adding custom params */
  allowAddParam?: boolean;
}

export interface ConnectionState extends Connection { }

// Legacy state types for UI compatibility
export interface InstanceState {
  definitionId: string;
  /*
    key: instanceId.nodeId.endpointId(:in/:out)
    value: always wrapped as object
  */
  values: Record<string, Record<string, any>>;
  viewport: ViewportState | null;
}

export interface DefinitionState {
  api: Record<string, any>;
  nodes: Record<string, NodeState>;
  connections: Record<string, ConnectionState>;
  positions: Record<string, Position>;
  vizNode?: string;
  info?: string;
}

export type InstanceStateMap = Record<string, InstanceState>;
export type DefinitionStateMap = Record<string, DefinitionState>;

export const initialDefinitionState: DefinitionState = {
  api: {},
  nodes: {},
  connections: {},
  positions: {}
};

export const initialInstanceState: InstanceState = {
  definitionId: 'root',
  values: {},
  viewport: null
};

// Values: [instanceId][nodeId][endpointId:direction] = value
export type ValuesMap = Record<string, Record<string, Record<string, any>>>;

// Positions: [definitionId][nodeId] = {x, y}
export type PositionsMap = Record<string, Record<string, Position>>;

// Viewports: [instanceId] = {x, y, zoom}
export type ViewportsMap = Record<string, ViewportState>;

// Definitions: [definitionId] = GraphDefinition
export type DefinitionsMap = Record<string, GraphDefinition>;

// Listener types
export type DefinitionListener = (definitionId: string, state: DefinitionState) => void;
export type ValuesListener = (instanceId: string, values: Record<string, Record<string, any>>) => void;
export type ReadyListener = (menuState: MenuState) => void;
export type BeforeDisposeListener = () => void;

/** Key for accessing endpoint values in the store */
export interface StoreKey {
  instancePath: string;
  nodeId: string;
  endpointId: string;
}

export interface IRuntimeStore {
  init: (session: SessionState, libraryDefinitions: Record<string, GraphDefinition>) => void

  //valueChanged: (args: ValueChangedArgs) => void
  registerInstance: (instanceId: string, definitionId: string) => void

  // Direct store access
  setDefinition: (definitionId: string, definition: GraphDefinition, newNodeIds?: string[]) => void
  setValue: (key: StoreKey, value: any) => any
  getValue: (key: StoreKey) => any
  getPayload: (key: StoreKey) => any
  getNodeValues: (instancePath: string, nodeId: string) => Record<string, any>
  ensureNodeEntry: (instancePath: string, nodeId: string) => void

  // Batch mode
  beginBatch: () => void
  endBatch: () => void
  isBatching: () => boolean
  emitValuesChanged: (instanceId: string) => void

  // Subscriptions
  subscribeDefinition: (listener: DefinitionListener) => () => void
  getDefinition: (definitionId: string) => GraphDefinition | undefined
  getPositions: (definitionId: string) => Record<string, Position>
  setPositions: (definitionId: string, positions: Record<string, Position>) => void
}

// ============ RuntimeStore ============

export class RuntimeStore implements IRuntimeStore {
  // State
  private definitions: DefinitionsMap = {};
  private positions: PositionsMap = {};
  private viewports: ViewportsMap = {};
  private values: ValuesMap = {};
  private instanceDefinitions: Record<string, string> = { root: 'root' }; // instanceId -> definitionId
  private performance: Record<string, PerformanceViewState> = {};
  private defaultView: DefaultView = 'graph';

  // Library definitions (from server)
  libraryDefinitions: Record<string, GraphDefinition> = {};

  // Plugin registry — set by Runtime after plugin registration
  private pluginRegistry: PluginRegistry | null = null;

  setPluginRegistry(registry: PluginRegistry): void {
    this.pluginRegistry = registry;
  }


  // Batch mode — suppresses all setValue writes (for repeater child instances)
  private batchDepth = 0;

  // Listeners
  private definitionListeners: Set<DefinitionListener> = new Set();
  private valuesListeners: Set<ValuesListener> = new Set();
  private readyListeners: Set<ReadyListener> = new Set();
  private beforeDisposeListeners: Set<BeforeDisposeListener> = new Set();

  // Callback for external state updates
  onExternalUpdate: (() => void) | null = null;

  // ============ Lifecycle ============

  /**
   * Clear state before loading a new project.
   * Keeps library definitions but clears positions, viewports, values, and definitions.
   */
  clearForNewProject(): void {
    // Notify UI to stop any running loops before clearing
    this.emitBeforeDispose();

    this.definitions = {};
    this.positions = {};
    this.viewports = {};
    this.values = {};
    this.instanceDefinitions = { root: 'root' };
    this.performance = {};
    this.defaultView = 'graph';
  }

  // ============ Batch Mode ============

  beginBatch(): void {
    this.batchDepth++;
  }

  endBatch(): void {
    if (this.batchDepth > 0) this.batchDepth--;
  }

  isBatching(): boolean {
    return this.batchDepth > 0;
  }

  // ============ Core Methods ============

  /**
   * Handle definition pushed from runtime.
   * Stores the definition directly and handles auto-layout for new nodes.
   * Positions are extracted from definition.positions if present.
   */
  public setDefinition(definitionId: string, definition: GraphDefinition, newNodeIds?: string[]): void {
    logger.trace('RuntimeStore', `[definitionChanged] ${definitionId}, connections: ${definition.connections.length}, nodes: ${Object.keys(definition.nodes).join(', ')}`);
    // Store definition directly (pushed from runtime)
    this.definitions[definitionId] = definition;

    // Ensure positions map exists for this definition
    if (!this.positions[definitionId]) {
      this.positions[definitionId] = {};
    }

    // Auto-layout new nodes without positions
    if (newNodeIds && newNodeIds.length > 0) {
      const currentPositions = this.positions[definitionId];
      const nodesWithoutPositions = newNodeIds.filter(id => !currentPositions[id]);

      if (nodesWithoutPositions.length > 0) {
        const allNodeIds = Object.keys(definition.nodes);
        const edges = definition.connections.map(conn => ({
          source: conn.source.split('.')[0],
          target: conn.target.split('.')[0]
        }));

        const layoutPositions = autoLayoutFromEdges(allNodeIds, edges);
        for (const nodeId of nodesWithoutPositions) {
          this.positions[definitionId][nodeId] = layoutPositions[nodeId] ?? { x: 0, y: 0 };
        }
        // Positions will be included in the definition state emission below
      }
    }

    this.emitDefinitionChanged(definitionId);
  }

  
  /**
   * Handle value change from runtime.
   * Updates both the base value and the activity key, emits once.
   */
  /*
  public valueChanged(args: ValueChangedArgs): void {
    const { instanceId, nodeId, endpointId, value, direction } = args;

    // Update base value and activity key
    this.setValueInternal(instanceId, nodeId, endpointId, value);
    this.setValueInternal(instanceId, nodeId, `${endpointId}:${direction}`, value);
    this.emitValuesChanged(instanceId);
  }*/

  // ============ Initialization ============

  init(session: SessionState, libraryDefinitions: Record<string, GraphDefinition>): void {
    // Store library definitions
    this.libraryDefinitions = libraryDefinitions;

    // Build primitive info from plugin registry
    const primitiveDefs: NodeTypeInfo[] = [];
    if (this.pluginRegistry) {
      for (const nodeDef of this.pluginRegistry.getAllNodeDefs()) {
        primitiveDefs.push({
          type: nodeDef.type,  // Already qualified by registry
          shortName: nodeDef.shortName,
          label: nodeDef.label,
          info: nodeDef.info,
          domain: nodeDef.domain,
          category: nodeDef.category,
          keywords: nodeDef.keywords,
        });
      }
    }

    // Apply positions from session - handle both legacy and new formats
    if (session.positions) {
      const firstValue = Object.values(session.positions)[0];
      const isLegacyFormat = firstValue && 'x' in firstValue && 'y' in firstValue;

      if (isLegacyFormat) {
        // Legacy: flat positions { nodeId: {x, y} }
        this.positions['root'] = session.positions as Record<string, Position>;
      } else {
        // New format: { definitionId: { nodeId: {x, y} } }
        for (const [defId, defPositions] of Object.entries(session.positions)) {
          this.positions[defId] = defPositions as Record<string, Position>;
        }
      }
      // Don't emit here - runtime will emit when it instantiates
    }

    // Apply viewport from session
    if (session.viewport && 'x' in session.viewport) {
      this.viewports['root'] = session.viewport as unknown as ViewportState;
    }

    // Emit ready with menu state
    this.emitReady({
      libDefIds: Object.keys(libraryDefinitions),
      primitiveDefs,
      projectDefIds: []
    });
  }



  // ============ Getters ============

  getDefinition(definitionId: string): GraphDefinition | undefined {
    return this.definitions[definitionId];
  }

  getDefinitionIds(): string[] {
    return Object.keys(this.definitions);
  }

  getPositions(definitionId: string): Record<string, Position> {
    return this.positions[definitionId] ?? {};
  }

  getViewport(definitionId: string): ViewportState | undefined {
    return this.viewports[definitionId];
  }

  // ============ Performance View State ============

  getInstanceDefinitionId(instanceId: string): string | undefined {
    return this.instanceDefinitions[instanceId];
  }

  getPerformanceView(defId: string): PerformanceViewState | undefined {
    return this.performance[defId];
  }

  setPerformanceView(defId: string, state: PerformanceViewState): void {
    this.performance[defId] = state;
  }

  getDefaultView(): DefaultView {
    return this.defaultView;
  }

  setDefaultView(mode: DefaultView): void {
    this.defaultView = mode;
    this.emitValuesChanged('root');
  }

  /**
   * Get full local session state for saving.
   * Includes all definitions used in the project for self-contained saves.
   * Positions are keyed by definitionId (not flat).
   */
  getLocalSession(): {
    definitions: Record<string, GraphDefinition>;
    positions: Record<string, Record<string, Position>>;
    viewport: Record<string, ViewportState>;
    performance: Record<string, PerformanceViewState>;
    defaultView: DefaultView;
  } {
    return {
      definitions: { ...this.definitions },
      positions: { ...this.positions },
      viewport: this.viewports,
      performance: { ...this.performance },
      defaultView: this.defaultView,
    };
  }

  getValues(instanceId: string): Record<string, Record<string, any>> {
    return this.values[instanceId] ?? {};
  }

  /**
   * Resolve instanceId shorthand: ~ -> root, ~/path -> root/path
   */
  private resolveInstanceId(instanceId: string): string {
    if (instanceId === '~') return 'root';
    if (instanceId.startsWith('~/')) return 'root' + instanceId.slice(1);
    return instanceId;
  }

  /**
   * Get the unwrapped value from the store.
   * Returns undefined if value not set.
   */
  getValue(key: StoreKey): any {
    const resolved = this.resolveInstanceId(key.instancePath);
    const instanceValues = this.values[resolved];
    if (!instanceValues) return undefined;
    const nodeValues = instanceValues[key.nodeId];
    if (!nodeValues) return undefined;
    const payload = nodeValues[key.endpointId];
    if (payload === undefined) return undefined;
    // Unwrap if it's a payload
    if (payload && typeof payload === 'object' && 'value' in payload) {
      return payload.value;
    }
    return payload;
  }

  /**
   * Get the full payload from the store (includes metadata like time, velocity, etc.).
   * Returns undefined if value not set.
   */
  getPayload(key: StoreKey): any {
    const resolved = this.resolveInstanceId(key.instancePath);
    const instanceValues = this.values[resolved];
    if (!instanceValues) return undefined;
    const nodeValues = instanceValues[key.nodeId];
    if (!nodeValues) return undefined;
    return nodeValues[key.endpointId];
  }

  /**
   * Set a value directly in the store (internal, no emit).
   * Creates new instance-level object so React detects changes.
   */
  private setValueInternal(instanceId: string, nodeId: string, key: string, value: any): void {
    const resolved = this.resolveInstanceId(instanceId);
    const instanceValues = this.values[resolved] ?? {};
    const nodeValues = instanceValues[nodeId] ?? {};
    nodeValues[key] = value;
    // Create new object at instance level (same pattern as old ReactiveStore.setState)
    this.values[resolved] = { ...instanceValues, [nodeId]: nodeValues };
  }

  /**
   * Ensure value is wrapped as a Payload.
   */
  private ensureWrapped(value: any): any {
    if (value && typeof value === 'object' && 'value' in value) {
      return value;
    }
    return { value };
  }

  /**
   * Set a value in the store and emit change.
   * Values are always stored as Payloads (wrapped: { value, time?, ... }).
   * Returns the wrapped payload so callers can use it.
   */
  setValue(key: StoreKey, value: any): any {
    const resolved = this.resolveInstanceId(key.instancePath);
    const wrapped = this.ensureWrapped(value);
    this.setValueInternal(resolved, key.nodeId, key.endpointId, wrapped);
    if (this.batchDepth === 0) {
      this.emitValuesChanged(resolved);
    }
    return wrapped;
  }

  /**
   * Ensure a node entry exists in the values store (even if empty).
   * Needed so UI doesn't throw when checking for node values.
   */
  ensureNodeEntry(instancePath: string, nodeId: string): void {
    const resolved = this.resolveInstanceId(instancePath);
    if (!this.values[resolved]) {
      this.values[resolved] = {};
    }
    if (!this.values[resolved][nodeId]) {
      this.values[resolved][nodeId] = {};
    }
  }

  /**
   * Get all values for a node in an instance.
   * Returns empty object if node has no values.
   */
  getNodeValues(instancePath: string, nodeId: string): Record<string, any> {
    const resolved = this.resolveInstanceId(instancePath);
    return this.values[resolved]?.[nodeId] ?? {};
  }

  // ============ UI State ============

  /**
   * Get definition state in legacy format.
   * Derives NodeState from primitives + definition config.
   */
  getDefinitionState(definitionId: string): DefinitionState {
    const definition = this.definitions[definitionId];
    if (!definition) {
      throw new Error(`Definition '${definitionId}' not found in store. Available: ${Object.keys(this.definitions).join(', ')}`);
    }

    // Build NodeState from primitives + definition config
    const nodes: Record<string, NodeState> = {};
    for (const [nodeId, nodeEntry] of Object.entries(definition.nodes)) {
      const nodeType = getNodeType(nodeEntry);
      const label = getNodeLabel(nodeEntry);
      nodes[nodeId] = this.buildNodeState(nodeId, nodeType, definition, label);
    }

    // Build connections map
    const connections: Record<string, ConnectionState> = {};
    for (const conn of definition.connections) {
      const key = `${conn.source}->${conn.target}`;
      connections[key] = conn;
    }

    // Ensure positions exist, auto-layout if needed
    if (!this.positions[definitionId]) {
      this.positions[definitionId] = {};
    }
    const allNodeIds = Object.keys(definition.nodes);
    const nodesWithoutPositions = allNodeIds.filter(id => !this.positions[definitionId][id]);
    if (nodesWithoutPositions.length > 0) {
      const edges = definition.connections.map(conn => ({
        source: conn.source.split('.')[0],
        target: conn.target.split('.')[0]
      }));
      const layoutPositions = autoLayoutFromEdges(allNodeIds, edges);
      for (const nodeId of nodesWithoutPositions) {
        this.positions[definitionId][nodeId] = layoutPositions[nodeId] ?? { x: 0, y: 0 };
      }
    }

    return {
      api: definition.api,
      nodes,
      connections,
      positions: this.positions[definitionId],
      vizNode: definition.vizNode,
      info: definition.info
    };
  }

  /**
   * Get instance state in legacy format.
   */
  getInstanceState(instanceId: string): InstanceState | undefined {
    const values = this.values[instanceId];
    const definitionId = this.instanceDefinitions[instanceId];

    return {
      definitionId,
      values,
      viewport: this.viewports[instanceId] ?? null
    };
  }

  /**
   * Register an instance's definition ID.
   * Called by runtime when instances are created.
   */
  registerInstance(instanceId: string, definitionId: string): void {
    this.instanceDefinitions[instanceId] = definitionId;
    if (!this.values[instanceId]) {
      this.values[instanceId] = {};
    }
    if (this.batchDepth === 0) {
      this.emitValuesChanged(instanceId);
    }
  }

  /**
   * Subscribe to instance state changes (legacy API).
   */
  subscribeInstance(listener: (instanceId: string, state: InstanceState) => void): () => void {
    const wrapper = (instanceId: string, values: Record<string, Record<string, any>>) => {
      const definitionId = this.instanceDefinitions[instanceId] ?? instanceId;
      listener(instanceId, { definitionId, values, viewport: this.viewports[instanceId] ?? null });
    };
    this.valuesListeners.add(wrapper);
    return () => this.valuesListeners.delete(wrapper);
  }

  /**
   * Check if an endpoint is exposed in a definition's API.
   */
  private isEndpointExposed(definition: GraphDefinition, nodeId: string, endpointId: string): boolean {
    const path = `${nodeId}.${endpointId}`;
    return Object.values(definition.api).some(exposed => exposed.path === path);
  }

  /**
   * Resolve endpoint metadata from an internal endpoint path.
   * Used to get type, min, max, etc. for exposed endpoints on subgraphs.
   */
  private resolveInternalEndpoint(subgraphDef: GraphDefinition, internalPath: string): Partial<EndpointState> | null {
    const [internalNodeId, internalEndpointId] = internalPath.split('.');
    const nodeEntry = subgraphDef.nodes[internalNodeId];
    if (!nodeEntry) return null;
    const internalNodeType = getNodeType(nodeEntry);

    // Check if internal node is a primitive
    const primitiveDef = this.pluginRegistry?.getNodeDef(internalNodeType);
    if (primitiveDef) {
      const endpointDef = primitiveDef.endpoints[internalEndpointId];

      if (endpointDef) {
        return {
          dataType: endpointDef.dataType ?? 'number',
          dataFlow: endpointDef.dataFlow ?? 'param',
          label: endpointDef.label,
          info: endpointDef.info,
          group: endpointDef.group,
          min: endpointDef.min ?? 0,
          max: endpointDef.max ?? 1,
          step: endpointDef.step ?? 0.01,
          options: [...(endpointDef.options ?? [])],
          optionLabels: [...(endpointDef.optionLabels ?? [])],
          optionInfo: [...(endpointDef.optionInfo ?? [])],
          scaleIncomingValues: endpointDef.scaleIncomingValues ?? false,
          inheritMetadata: endpointDef.inheritMetadata ?? false
        };
      }

      // Custom param — not in base endpoints, resolve from subgraph config
      const configEntry = subgraphDef.config[internalNodeId]?.[internalEndpointId];
      if (configEntry && typeof configEntry === 'object' && !Array.isArray(configEntry)) {
        return {
          dataType: configEntry.dataType ?? 'number',
          dataFlow: configEntry.dataFlow ?? 'param',
          label: configEntry.label,
          min: configEntry.min ?? 0,
          max: configEntry.max ?? 99,
          step: configEntry.step ?? 1,
          options: [...(configEntry.options ?? [])],
          optionLabels: [...(configEntry.optionLabels ?? [])],
          optionInfo: [...(configEntry.optionInfo ?? [])],
          scaleIncomingValues: configEntry.scaleIncomingValues ?? false,
          inheritMetadata: configEntry.inheritMetadata ?? false
        };
      }
    }

    // Check if internal node is another subgraph
    const nestedSubgraphDef = this.definitions[internalNodeType];
    if (nestedSubgraphDef?.api) {
      const nestedExposed = nestedSubgraphDef.api[internalEndpointId];
      if (nestedExposed) {
        // Recursively resolve through nested subgraphs
        return this.resolveInternalEndpoint(nestedSubgraphDef, nestedExposed.path);
      }
    }

    return null;
  }

  /**
   * Build NodeState from primitive definition + definition config.
   * No runtime access needed - derives everything from static data.
   */
  private buildNodeState(nodeId: string, nodeType: string, definition: GraphDefinition, label?: string): NodeState {
    const endpoints: Record<string, EndpointState> = {};

    // Get primitive definition for this node type
    const primitiveDef = this.pluginRegistry?.getNodeDef(nodeType);

    // Subgraph definition must exist if not a primitive
    if (!primitiveDef && !this.definitions[nodeType]) {
      const msg = `Subgraph definition '${nodeType}' not found for node '${nodeId}'. Available definitions: ${Object.keys(this.definitions).join(', ')}`;
      logger.error('RuntimeStore', msg);
      throw new Error(msg);
    }

    if (primitiveDef) {
      // Primitive node - build endpoints from primitive definition
      for (const [eId, e] of Object.entries(primitiveDef.endpoints)) {
        // Get config overrides if any (cast for property access)
        const configOverrides = (definition.config?.[nodeId]?.[eId] ?? {}) as Partial<Endpoint>;

        // useExp flag: config override > base endpoint useExp
        const useExp: boolean = configOverrides.useExp ?? e.useExp ?? false;
        const exprSource = (configOverrides as any).exp as string ?? e.exp;

        endpoints[eId] = {
          id: eId,
          dataType: e.dataType ?? 'number',
          dataFlow: e.dataFlow ?? 'param',
          label: configOverrides.label ?? e.label,
          info: e.info,
          min: configOverrides.min ?? e.min ?? 0,
          max: configOverrides.max ?? e.max ?? 99,
          step: configOverrides.step ?? e.step ?? 1,
          options: [...(configOverrides.options ?? e.options ?? [])],
          optionLabels: [...(configOverrides.optionLabels ?? e.optionLabels ?? [])],
          optionInfo: [...(configOverrides.optionInfo ?? e.optionInfo ?? [])],
          isDisabled: false,
          isExposed: this.isEndpointExposed(definition, nodeId, eId),
          isInput: configOverrides.isInput ?? e.isInput ?? false,
          isOutput: configOverrides.isOutput ?? e.isOutput ?? false,
          allowInput: e.allowInput ?? true,
          allowOutput: e.allowOutput ?? true,
          scaleIncomingValues: configOverrides.scaleIncomingValues ?? e.scaleIncomingValues ?? false,
          inheritMetadata: configOverrides.inheritMetadata ?? e.inheritMetadata ?? false,
          accepts: configOverrides.accepts ?? e.accepts ?? [e.dataType ?? 'number'],
          readonly: (configOverrides as any).readonly ?? e.readonly ?? false,
          group: e.group,
          length: e.length,
          order: e.order,
          primary: e.primary,
          ...(useExp ? { useExp, exp: exprSource } : exprSource ? { exp: exprSource } : undefined),
          ...((configOverrides as any).control ? { control: (configOverrides as any).control } : undefined),
          stringify: e.stringify ?? (() => ''),
          tweakable: (configOverrides as any).tweakable ?? false,
        };
      }

      // Add dynamic endpoints if node allows them
      if (primitiveDef.allowAddParam) {
        const nodeConfig = definition.config?.[nodeId] ?? {};
        for (const [eId, config] of Object.entries(nodeConfig)) {
          // Skip if already a base endpoint
          if (primitiveDef.endpoints[eId]) continue;
          // Object configs without a base endpoint become dynamic param endpoints
          if (config && typeof config === 'object' && !Array.isArray(config)) {
            const processed = params({ [eId]: config as Partial<Endpoint> })[eId];
            const useExp: boolean = (config as any).useExp ?? false;
            const exprSource = (config as any).exp as string | undefined;
            endpoints[eId] = {
              id: eId,
              dataType: processed.dataType ?? 'number',
              dataFlow: (config as any).dataFlow ?? 'param',
              label: processed.label ?? eId,
              info: processed.info,
              group: (config as any).group,
              min: processed.min ?? 0,
              max: processed.max ?? 99,
              step: processed.step ?? 1,
              options: [...(processed.options ?? [])],
              optionLabels: [...(processed.optionLabels ?? [])],
              optionInfo: [...(processed.optionInfo ?? [])],
              isDisabled: false,
              isExposed: this.isEndpointExposed(definition, nodeId, eId),
              isInput: processed.isInput ?? false,
              isOutput: processed.isOutput ?? false,
              allowInput: processed.allowInput ?? true,
              allowOutput: processed.allowOutput ?? true,
              scaleIncomingValues: processed.scaleIncomingValues ?? false,
              inheritMetadata: processed.inheritMetadata ?? false,
              accepts: processed.accepts ?? [processed.dataType ?? 'number'],
              readonly: processed.readonly ?? false,
              stringify: processed.stringify ?? (() => ''),
              primary: false,
              custom: true,
              tweakable: (config as any).tweakable ?? false,
              ...(useExp ? { useExp, exp: exprSource } : exprSource ? { exp: exprSource } : undefined)
            };
          }
        }
      }
    } else {
      // Subgraph node - build virtual endpoints from the subgraph's API
      const subgraphDef = this.definitions[nodeType];
      if (subgraphDef?.api) {
        for (const [exposedName, exposed] of Object.entries(subgraphDef.api)) {
          // Resolve type and metadata from internal endpoint
          const internalMeta = this.resolveInternalEndpoint(subgraphDef, exposed.path);
          // Get config overrides from containing definition (cast for property access)
          const configOverrides = (definition.config?.[nodeId]?.[exposedName] ?? {}) as Partial<Endpoint>;

          // Priority: configOverrides (per-instance) > exposed (api, shared) > internalMeta (primitive default)
          const useExp: boolean = configOverrides.useExp ?? false;
          const exprSource = (configOverrides as any).exp as string | undefined;
          endpoints[exposedName] = {
            id: exposedName,
            dataType: internalMeta.dataType ?? 'number',
            dataFlow: internalMeta.dataFlow ?? 'param',
            label: exposed.label ?? exposedName,
            info: exposed.info ?? internalMeta?.info,
            group: exposed.group ?? internalMeta?.group,
            min: configOverrides.min ?? exposed.min ?? internalMeta?.min ?? 0,
            max: configOverrides.max ?? exposed.max ?? internalMeta?.max ?? 99,
            step: configOverrides.step ?? exposed.step ?? internalMeta?.step ?? 1,
            options: [...(configOverrides.options ?? exposed.options ?? internalMeta?.options ?? [])],
            optionLabels: [...(configOverrides.optionLabels ?? exposed.optionLabels ?? internalMeta?.optionLabels ?? [])],
            optionInfo: [...(configOverrides.optionInfo ?? exposed.optionInfo ?? internalMeta?.optionInfo ?? [])],
            isDisabled: false,
            primary: false,
            isExposed: this.isEndpointExposed(definition, nodeId, exposedName),
            isInput: configOverrides.isInput ?? exposed.isInput ?? false,
            isOutput: configOverrides.isOutput ?? exposed.isOutput ?? false,
            allowInput: exposed.allowInput ?? true,
            allowOutput: exposed.allowOutput ?? true,
            scaleIncomingValues: configOverrides.scaleIncomingValues ?? exposed.scaleIncomingValues ?? internalMeta?.scaleIncomingValues ?? false,
            inheritMetadata: configOverrides.inheritMetadata ?? exposed.inheritMetadata ?? internalMeta?.inheritMetadata ?? false,
            accepts: configOverrides.accepts ?? exposed.accepts ?? internalMeta?.accepts ?? [internalMeta?.dataType ?? 'number'],
            readonly: (configOverrides as any).readonly ?? (exposed as any).readonly ?? internalMeta?.readonly ?? false,
            ...(useExp ? { useExp, exp: exprSource } : exprSource ? { exp: exprSource } : undefined),
            stringify: internalMeta?.stringify ?? (() => ''),
            tweakable: (configOverrides as any).tweakable ?? false,
          };
        }
      }
    }

    // Get info from primitive or subgraph definition
    const info = primitiveDef?.info ?? this.definitions[nodeType]?.info;

    return {
      id: nodeId,
      type: nodeType,
      label: label ?? primitiveDef?.label ?? primitiveDef?.shortName,
      info,
      endpoints,
      logLevel: LogLevel.Info, // Default log level
      allowAddParam: primitiveDef?.allowAddParam,
    };
  }

  // ============ Setters ============

  setPositions(definitionId: string, positions: Record<string, Position>): void {
    this.positions[definitionId] = positions;
    // Only emit if definition exists (skip during project load before runtime)
    if (this.definitions[definitionId]) {
      this.emitPositionsChanged(definitionId);
    }
  }

  savePosition(definitionId: string, nodeId: string, x: number, y: number): void {
    if (!this.positions[definitionId]) {
      this.positions[definitionId] = {};
    }
    // Preserve isCollapsed when updating position
    const existing = this.positions[definitionId][nodeId];
    this.positions[definitionId][nodeId] = { x, y, isCollapsed: existing?.isCollapsed };
    this.emitPositionsChanged(definitionId);
  }

  setNodeCollapsed(definitionId: string, nodeId: string, isCollapsed: boolean): void {
    if (!this.positions[definitionId]) {
      this.positions[definitionId] = {};
    }
    const existing = this.positions[definitionId][nodeId] ?? { x: 0, y: 0 };
    if (isCollapsed) {
      this.positions[definitionId][nodeId] = { ...existing, isCollapsed };
    } else {
      // Remove isCollapsed when false to keep positions clean
      const { isCollapsed: _, ...rest } = existing;
      this.positions[definitionId][nodeId] = rest as Position;
    }
    this.emitPositionsChanged(definitionId);
  }

  setViewport(instanceId: string, viewport: ViewportState): void {
    this.viewports[instanceId] = viewport;
    // Viewport is part of InstanceState - emit values changed to notify listeners
    this.emitValuesChanged(instanceId);
  }

  // ============ Subscriptions ============

  subscribeDefinition(listener: DefinitionListener): () => void {
    this.definitionListeners.add(listener);
    return () => this.definitionListeners.delete(listener);
  }

  subscribeValues(listener: ValuesListener): () => void {
    this.valuesListeners.add(listener);
    return () => this.valuesListeners.delete(listener);
  }


  subscribeReady(listener: ReadyListener): () => void {
    this.readyListeners.add(listener);
    return () => this.readyListeners.delete(listener);
  }

  subscribeBeforeDispose(listener: BeforeDisposeListener): () => void {
    this.beforeDisposeListeners.add(listener);
    return () => this.beforeDisposeListeners.delete(listener);
  }

  emitBeforeDispose(): void {
    for (const listener of this.beforeDisposeListeners) {
      listener();
    }
  }

  // ============ Emitters ============

  private emitDefinitionChanged(definitionId: string)  {
    const state = this.getDefinitionState(definitionId);
    logger.trace('RuntimeStore', `[emitDefinitionChanged] ${definitionId}, connections: ${state ? Object.keys(state.connections).length : 0}, listeners: ${this.definitionListeners.size}`);

    for (const listener of this.definitionListeners) {
      listener(definitionId, state);
    }
  }

  emitValuesChanged(instanceId: string) {
    const values = this.values[instanceId];

    for (const listener of this.valuesListeners) {
      listener(instanceId, values);
    }

  }

  private emitPositionsChanged(definitionId: string): void {
    // Positions are part of DefinitionState, so just emit definition changed
    this.emitDefinitionChanged(definitionId);
  }


  private emitReady(menuState: MenuState): void {
    for (const listener of this.readyListeners) {
      listener(menuState);
    }
  }



  dispose(): void {
    this.definitionListeners.clear();
    this.valuesListeners.clear();
    this.readyListeners.clear();
  }
}
